// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — UserManagementPage (Admin)
// CRUD operations for users
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Search, Edit3, Trash2, Check
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Button, Input, Select, Badge, Toggle } from '@components/ui/Primitives';
import { Modal } from '@components/ui/Modal';
import { LoadingSpinner } from '@components/ui/States';

export function UserManagementPage() {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    email: '', full_name_ar: '', full_name_en: '',
    role: 'customer', tenant_id: '', is_active: true, phone: ''
  });

  // Load users and tenants
  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: usersData }, { data: tenantsData }] = await Promise.all([
      supabase.from('profiles').select('*, tenants(name_ar, name_en)').order('created_at', { ascending: false }),
      supabase.from('tenants').select('id, name_ar, name_en')
    ]);
    setUsers(usersData || []);
    setTenants(tenantsData || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = users.filter(u => {
    const q = searchQuery.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.full_name_ar || '').toLowerCase().includes(q) ||
      (u.full_name_en || '').toLowerCase().includes(q)
    );
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setForm({
      email: '', full_name_ar: '', full_name_en: '',
      role: 'customer', tenant_id: '', is_active: true, phone: ''
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      full_name_ar: user.full_name_ar || '',
      full_name_en: user.full_name_en || '',
      role: user.role,
      tenant_id: user.tenant_id || '',
      is_active: user.is_active,
      phone: user.phone || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (editingUser) {
      // Update existing
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name_ar: form.full_name_ar,
          full_name_en: form.full_name_en,
          role: form.role,
          tenant_id: form.tenant_id || null,
          is_active: form.is_active,
          phone: form.phone
        })
        .eq('id', editingUser.id);
      if (!error) {
        setShowModal(false);
        loadData();
      }
    } else {
      // Create user (note: actual auth user creation must be done via admin API)
      alert(lang === 'ar'
        ? 'يُنصح بإنشاء المستخدم من Supabase Dashboard أولاً، ثم تحديث بياناته هنا'
        : 'Please create the user in Supabase Auth Dashboard first, then update details here'
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    const { error } = await supabase.from('profiles').delete().eq('id', id);
    if (!error) loadData();
  };

  const getRoleVariant = (role) => {
    if (role === 'super_admin') return 'danger';
    if (role === 'employee') return 'info';
    return 'primary';
  };

  const getRoleLabel = (role) => t[role] || role;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        icon={<Users size={22} color={theme.primary} />}
        title={t.userManagement}
        description={lang === 'ar' ? 'إدارة المستخدمين — بدون تسجيل ذاتي' : 'Full CRUD — No public signup'}
        actions={
          <Button onClick={openCreateModal}>
            <Plus size={14} /> {t.createUser}
          </Button>
        }
      />

      <Card style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute',
              [isRTL ? 'right' : 'left']: 12,
              top: 12,
              color: theme.textTertiary
            }} />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t.searchUsers}
              style={{ [isRTL ? 'paddingRight' : 'paddingLeft']: 34 }}
            />
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {[t.fullName, t.email, t.role, t.tenant, t.status, t.actions].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    letterSpacing: 0.5
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.text
                  }}>
                    {lang === 'ar' ? u.full_name_ar : u.full_name_en}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: theme.textSecondary }}>
                    {u.email}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge variant={getRoleVariant(u.role)}>{getRoleLabel(u.role)}</Badge>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: theme.textSecondary }}>
                    {u.tenants
                      ? (lang === 'ar' ? u.tenants.name_ar : u.tenants.name_en)
                      : '—'
                    }
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge variant={u.is_active ? 'success' : 'warning'}>
                      {u.is_active ? t.active : t.inactive}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(u)}>
                        <Edit3 size={13} />
                      </Button>
                      {u.role !== 'super_admin' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)}>
                          <Trash2 size={13} color={theme.danger} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? t.editUser : t.createUser}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            value={form.full_name_ar}
            onChange={e => setForm(f => ({ ...f, full_name_ar: e.target.value }))}
            placeholder={lang === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)'}
          />
          <Input
            value={form.full_name_en}
            onChange={e => setForm(f => ({ ...f, full_name_en: e.target.value }))}
            placeholder={lang === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'}
          />
          <Input
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder={t.email}
            type="email"
            disabled={!!editingUser}
          />
          <Input
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder={t.phone}
            type="tel"
          />
          <Select
            value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            options={[
              { value: 'super_admin', label: t.superAdmin },
              { value: 'owner', label: t.owner },
              { value: 'manager', label: t.manager },
              { value: 'employee', label: t.employee },
              { value: 'accountant', label: t.accountant },
              { value: 'customer', label: t.customer }
            ]}
          />
          {(form.role === 'customer' || form.role === 'owner' || form.role === 'manager') && (
            <Select
              value={form.tenant_id}
              onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value }))}
              options={[
                { value: '', label: t.none },
                ...tenants.map(tn => ({
                  value: tn.id,
                  label: lang === 'ar' ? tn.name_ar : tn.name_en
                }))
              ]}
            />
          )}
          <Toggle
            checked={form.is_active}
            onChange={v => setForm(f => ({ ...f, is_active: v }))}
            label={t.accountActive}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Button variant="ghost" onClick={() => setShowModal(false)}>{t.cancel}</Button>
            <Button onClick={handleSave}>
              <Check size={13} /> {editingUser ? t.update : t.create}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
